import { defineStore, storeToRefs } from 'pinia';
import { computed, reactive, ref, watch, watchEffect } from 'vue';
import WidgetApiService from '@/common/services/widget-api.service';
import { useAuthStore } from '@/store/modules/auth';
import { useGeneralStore } from '@/store/modules/general';
import { usePaymentStore } from '@/store/modules/payment';
import useTransactionsStore from '@/store/modules/transactions';
import { capitalizeFirstLetter } from '@/utils/coins';
import { addCountryPrefix, getChannelType, maskString, nameIsInvalid, validateMobileNumber, } from '@/utils/helpers';
import { CountryTypes } from '@/types/country';
import { PayoutAccountsTypes } from '@/types/payout-accounts';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
var ChannelTypes = WidgetApiServiceTypes.ChannelTypes;
var PayoutComponents = PayoutAccountsTypes.PayoutComponents;
var PayoutFlow = PayoutAccountsTypes.PayoutFlow;
var SupportedCountriesConfig = CountryTypes.SupportedCountriesConfig;
export const usePayoutAccountsStore = defineStore('payout-accounts', () => {
    const activePayoutAccount = reactive({});
    const error = ref('');
    const currentStep = ref(PayoutComponents.ManagePayouts);
    const loading = ref('');
    const selectedPayoutMethod = ref('');
    const payoutAccounts = ref([]);
    const payoutFlow = ref(PayoutFlow.Create);
    const fromTransaction = ref(false);
    const authStore = useAuthStore();
    const { token, partnerSessionId, deviceId } = storeToRefs(authStore);
    const generalStore = useGeneralStore();
    const { showToast } = generalStore;
    const paymentsStore = usePaymentStore();
    const { paymentNetworks, selectedPaymentNetwork, sellPaymentAccountName, sellPaymentAccountNumber, sellPaymentBranchCode, } = storeToRefs(paymentsStore);
    const transactionsStore = useTransactionsStore();
    const { countryConfig, channel, loadingMessage } = storeToRefs(transactionsStore);
    const countryConfigOptions = computed(() => [
        {
            value: countryConfig.value.countryCode,
            label: countryConfig.value.countryCode,
            country: countryConfig.value.country,
        },
    ]);
    const defaultCountry = computed(() => countryConfigOptions.value[0]);
    const isNG = computed(() => defaultCountry.value.country === CountryTypes.CountryCodes.NIGERIA);
    const createSteps = computed(() => ({
        [PayoutComponents.ManagePayouts]: {
            component: PayoutComponents.ManagePayouts,
            title: (title) => title || 'Saved payout accounts',
        },
        [PayoutComponents.SelectPayout]: {
            component: PayoutComponents.SelectPayout,
            title: (title) => title || 'Add new account',
        },
        [PayoutComponents.FormSelect]: {
            component: `${capitalizeFirstLetter(selectedPayoutMethod.value)}Form`,
            title: (title) => title || 'Your payment details',
        },
    }));
    const updateSteps = computed(() => ({
        [PayoutComponents.ManagePayouts]: {
            component: PayoutComponents.ManagePayouts,
            title: (title) => title || 'Saved payout accounts',
        },
        [PayoutComponents.FormSelect]: {
            component: `${capitalizeFirstLetter(selectedPayoutMethod.value)}Form`,
            title: (title) => title || 'Your payment details',
        },
        [PayoutComponents.DeleteConfirmation]: {
            component: PayoutComponents.DeleteConfirmation,
            title: (title) => title || 'Delete saved account',
        },
    }));
    const isCreateFlow = computed(() => payoutFlow.value === PayoutFlow.Create);
    const isUpdateFlow = computed(() => payoutFlow.value === PayoutFlow.Update);
    const getSteps = computed(() => isCreateFlow.value ? createSteps.value : updateSteps.value);
    const getStepsProps = computed(() => getSteps.value[currentStep.value]);
    const currentStepIndex = computed(() => Object.keys(getSteps.value).indexOf(currentStep.value));
    const getChannel = (accountType) => getChannelType(accountType);
    const isBank = computed(() => getChannel(selectedPayoutMethod.value) === ChannelTypes.BANK);
    const isMomo = computed(() => getChannel(selectedPayoutMethod.value) === ChannelTypes.MOMO);
    const selectedCountryConfig = computed(() => SupportedCountriesConfig[countryConfig.value.country]);
    const hasMomoSupport = computed(() => selectedCountryConfig.value.providers.find(provider => getChannel(provider) === ChannelTypes.MOMO));
    const hasBankSupport = computed(() => selectedCountryConfig.value.providers.find(provider => getChannel(provider) === ChannelTypes.BANK));
    const manualBankName = computed(() => hasBankSupport.value && selectedCountryConfig.value.manualBankSellDetails);
    const rules = computed(() => ({
        mobileNumber: (value) => {
            if (!value)
                return ' ';
            if (validateMobileNumber(value, selectedCountryConfig.value?.phoneValidation.MIN_PHONE_LENGTH, selectedCountryConfig.value?.phoneValidation.MAX_PHONE_LENGTH) !== true)
                return ' ';
            return true;
        },
        accountName: (value) => {
            if (!value)
                return ' ';
            if (nameIsInvalid(value))
                return ' ';
            return true;
        },
        selectedBankName: (value) => {
            if (!value)
                return ' ';
            return true;
        },
        bankAccountNumber: (value) => {
            if (!value)
                return ' ';
            if (value.length < 10)
                return ' ';
            return true;
        },
    }));
    const networks = computed(() => {
        if (!paymentNetworks.value?.length)
            return [];
        return [
            ...new Set(paymentNetworks.value.map(network => isBank.value ? network.name : network.code)),
        ];
    });
    const setStepIndex = (index) => {
        if (index === -1)
            return;
        currentStep.value = Object.keys(getSteps.value)[index];
    };
    const setLoading = (value) => {
        loading.value = value || '';
    };
    const setError = (value) => {
        error.value = value || '';
    };
    const generateToastText = (action) => {
        const payoutMethod = isBank.value ? 'Bank' : 'Mobile Money';
        return `You have ${action} ${payoutMethod} (${maskString(activePayoutAccount.accountNumber)})`;
    };
    const updatePayoutAccountField = (key, source, target) => {
        const sourceKey = key;
        const targetKey = key;
        if (source[sourceKey]) {
            target[targetKey] = source[sourceKey];
        }
    };
    const setActivePayoutAccount = (source) => {
        Object.keys(source).forEach(key => {
            updatePayoutAccountField(key, source, activePayoutAccount);
        });
    };
    const resetActivePayoutAccount = () => {
        paymentNetworks.value = [];
        selectedPaymentNetwork.value = null;
        sellPaymentAccountName.value = '';
        sellPaymentAccountNumber.value = '';
        sellPaymentBranchCode.value = '';
        Object.keys(activePayoutAccount).reduce((acc, key) => {
            acc[key] = undefined;
            return acc;
        }, activePayoutAccount);
    };
    const resetStep = () => {
        resetActivePayoutAccount();
        currentStep.value = createSteps.value.ManagePayouts.component;
        selectedPayoutMethod.value = '';
        payoutFlow.value = PayoutFlow.Create;
        setError();
        setLoading();
    };
    const getNetworks = async () => {
        try {
            setError();
            setLoading('Loading providers...');
            const accountType = isMomo.value ? 'phone' : 'bank';
            loadingMessage.value = loading.value;
            paymentNetworks.value = [];
            await paymentsStore.getNetworks({
                country: manualBankName.value
                    ? undefined
                    : selectedCountryConfig.value.countryCode,
                accountType: manualBankName.value ? undefined : accountType,
                channelId: manualBankName.value ? channel.value?.id : undefined,
            });
            paymentNetworks.value?.sort((netA, netB) => netA.name > netB.name ? 1 : -1);
        }
        catch (e) {
            setError();
            throw e;
        }
        finally {
            setLoading();
        }
    };
    const getPayoutAccounts = async () => {
        try {
            payoutAccounts.value = [];
            setError();
            setLoading('Loading accounts');
            const { accounts } = await WidgetApiService.GetPayoutAccounts(token.value, partnerSessionId.value, deviceId.value);
            payoutAccounts.value = accounts;
        }
        catch (e) {
            setError('Cannot fetch accounts');
            throw e;
        }
        finally {
            setLoading();
        }
    };
    const addPayoutAccount = async () => {
        try {
            setError();
            setLoading('Saving...');
            await WidgetApiService.AddPayoutAccount(token.value, partnerSessionId.value, deviceId.value, {
                ...activePayoutAccount,
                ...(isMomo.value
                    ? {
                        accountNumber: addCountryPrefix(activePayoutAccount.accountNumber, defaultCountry.value.label),
                        accountType: ChannelTypes.MOMO,
                    }
                    : {
                        accountType: ChannelTypes.BANK,
                        accountName: activePayoutAccount.accountName ||
                            sellPaymentAccountName.value,
                    }),
                country: selectedCountryConfig.value.countryName,
                currency: selectedCountryConfig.value.currency,
            });
            showToast(generateToastText('added'));
        }
        catch (e) {
            setError('Error saving account');
            throw e;
        }
        finally {
            setLoading();
        }
    };
    const updatePayoutAccount = async () => {
        try {
            setError();
            setLoading('Updating...');
            activePayoutAccount.createdAt = undefined;
            activePayoutAccount.updatedAt = undefined;
            await WidgetApiService.UpdatePayoutAccount(token.value, partnerSessionId.value, deviceId.value, {
                ...activePayoutAccount,
                ...(isMomo.value
                    ? {
                        accountNumber: addCountryPrefix(activePayoutAccount.accountNumber, defaultCountry.value.label),
                    }
                    : {}),
            });
            showToast(generateToastText('updated'));
        }
        catch (e) {
            setError('Error updating account');
            throw e;
        }
        finally {
            setLoading();
        }
    };
    const deletePayoutAccount = async () => {
        try {
            setError();
            setLoading('Deleting...');
            await WidgetApiService.DeletePayoutAccount(token.value, partnerSessionId.value, deviceId.value, {
                id: activePayoutAccount.id,
            });
            showToast(generateToastText('deleted'));
        }
        catch (e) {
            setError('Error deleting account');
            throw e;
        }
        finally {
            setLoading();
        }
    };
    watchEffect(() => {
        if (activePayoutAccount.accountNumber && isNG.value) {
            sellPaymentAccountNumber.value = activePayoutAccount.accountNumber;
        }
        if (sellPaymentAccountName.value && !manualBankName.value && isNG.value) {
            activePayoutAccount.accountName = sellPaymentAccountName.value;
        }
    });
    watch(currentStep, () => {
        setError();
        setLoading();
    });
    return {
        activePayoutAccount,
        addPayoutAccount,
        countryConfigOptions,
        currentStep,
        currentStepIndex,
        defaultCountry,
        fromTransaction,
        deletePayoutAccount,
        error,
        getChannel,
        getNetworks,
        getSteps,
        getStepsProps,
        getPayoutAccounts,
        hasBankSupport,
        hasMomoSupport,
        isBank,
        isCreateFlow,
        isMomo,
        isNG,
        isUpdateFlow,
        loading,
        manualBankName,
        networks,
        paymentNetworks,
        payoutFlow,
        payoutAccounts,
        rules,
        selectedPaymentNetwork,
        selectedPayoutMethod,
        setActivePayoutAccount,
        setLoading,
        setError,
        setStepIndex,
        resetStep,
        resetActivePayoutAccount,
        updatePayoutAccount,
    };
});
export default usePayoutAccountsStore;
